/**
 * User Properties Management
 *
 * Utilities for managing GA4 user properties via GTM dataLayer.
 * User IDs are hashed for privacy before being sent to analytics.
 */

import { pushToDataLayer } from './dataLayer';
import type { UserProperties } from '../../types/analytics';

/**
 * Hash a user ID for privacy.
 * This provides a one-way transformation that:
 * - Protects PII (user IDs are not sent in plain text)
 * - Maintains consistency (same input always produces same output for cross-device attribution)
 * - Is not reversible (cannot recover original ID from hash)
 *
 * Note: This is not for security purposes, just privacy/obfuscation.
 *
 * @param userId - The raw user ID (e.g., Supabase UUID)
 * @returns A hashed string representation
 */
export function hashUserId(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Set user properties in GA4 via dataLayer.
 * These become user-scoped dimensions in GA4 reports.
 *
 * @param properties - Partial user properties to set
 */
export function setUserProperties(properties: Partial<UserProperties>): void {
  const sanitizedProps: Record<string, unknown> = {};

  if (properties.user_id) {
    sanitizedProps.user_id = hashUserId(properties.user_id);
  }
  if (properties.subscription_status) {
    sanitizedProps.subscription_status = properties.subscription_status;
  }
  if (properties.subscription_plan) {
    sanitizedProps.subscription_plan = properties.subscription_plan;
  }
  if (properties.lifetime_stagings !== undefined) {
    sanitizedProps.lifetime_stagings = properties.lifetime_stagings;
  }

  pushToDataLayer({
    event: 'set_user_properties',
    ...sanitizedProps,
  });
}

/**
 * Identify a user in analytics.
 * Call this after successful authentication.
 *
 * @param userId - The raw user ID to identify
 */
export function identifyUser(userId: string): void {
  pushToDataLayer({
    event: 'user_identified',
    user_id: hashUserId(userId),
  });
}

/**
 * Clear user identity from analytics.
 * Call this on logout.
 */
export function clearUserIdentity(): void {
  pushToDataLayer({
    event: 'user_cleared',
    user_id: undefined,
  });
}
